import { ReactNode } from 'react';
import { Pressable } from 'react-native';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Card } from '@/common/components/Card/Card';
import { Icon } from '@/common/components/Icons/Icon';
import { Label } from '@/common/components/Label/Label';
import { Stack } from '@/common/components/Stack/Stack';
import * as CheckboxPrimitive from '@rn-primitives/checkbox';
import { styles } from './Checkbox.styles';

export type TCheckboxProps = {
  label: ReactNode;
  description?: ReactNode;
  error?: string;
  variant?: 'default' | 'card';
  className?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
};

export function Checkbox({
  checked,
  onChange,
  label,
  description,
  error,
  variant = 'default',
  disabled,
  className,
}: TCheckboxProps) {
  const hint = error || description;

  const content = (
    <Stack horizontal gap="sm">
      <CheckboxPrimitive.Root
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        className={styles.checkbox({ checked, disabled })}
      >
        <CheckboxPrimitive.Indicator>
          <Icon
            icon="check"
            size={12}
            color={disabled ? 'neutral20' : 'neutral30'}
          />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      <Stack shrink>
        <Label bold color={disabled ? 'neutral20' : 'neutral30'}>
          {label}
        </Label>
        {hint && (
          <BodyText size="sm" color={error ? 'systemError' : 'neutral20'}>
            {hint}
          </BodyText>
        )}
      </Stack>
    </Stack>
  );

  const parentProps = {
    className: styles.root({
      variant,
      disabled,
      error: !!error,
      className,
    }),
    onPress: !disabled ? () => onChange(!checked) : undefined,
  };

  if (variant === 'card') {
    return (
      <Card {...parentProps} plain={disabled}>
        {content}
      </Card>
    );
  }

  return <Pressable {...parentProps}>{content}</Pressable>;
}
